import { classNames, testIdGen, Body, Bold } from '@panwds/react-ui'

import type { ReactElement, ReactNode } from 'react'

export interface CardHeaderProps {
  /**
   * Actions
   */
  actions?: ReactNode;
  /**
   * Ignored prop, will be deprecated in the next major version.
   */
  appearance?: string;
  /**
   * Add data-metrics to the card header
   */
  dataMetrics?: string;
  /**
   * Add a data test id to the card header
   */
  dataTestId?: string;
  /**
   * Add an icon to the header.
   */
  icon?: ReactNode;
  /**
   * Ignored prop, will be deprecated in the next major version.
   */
  iconLeft?: ReactNode;
  /**
   * Ignored prop, will be deprecated in the next major version.
   */
  level?: number;
  /**
   * Describes the purpose of the card.
   */
  title?: ReactNode;
  /**
   * Refines the purpose of the card
   */
  subTitle?: ReactNode;
  /**
   * Add a className to the card header.
   */
  addClassName?: string;
}

/**
 * A styled container to be used within a Card.
 */
export function CardHeader({
  addClassName,
  actions,
  icon,
  dataMetrics,
  dataTestId,
  title,
  subTitle,
}: CardHeaderProps): ReactElement {
  return (
    <div
      data-metrics={dataMetrics}
      data-testid={
        dataTestId ||
        `card-header-${typeof title === 'string' && testIdGen(title)}`
      }
      className={classNames(
        'tw-flex tw-flex-wrap tw-rounded-t tw-px-4 tw-py-2',
        addClassName
      )}
    >
      <div className="tw-w-full tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-items-center">
          {icon && (
            <div className="tw-self-start tw-text-gray-600 dark:tw-text-gray-400">
              {icon}
            </div>
          )}
          <div className={`tw-flex tw-flex-col ${icon ? 'tw-ml-0.5' : ''}`}>
            <Bold
              data-testid="card-title"
              className="tw-font-lato tw-font-semibold tw-text-base tw-text-gray-900 dark:tw-text-gray-100"
            >
              {title}
            </Bold>
            {subTitle && (
              <Body
                data-testid="card-subtitle"
                size="xs"
                addClassName="tw-w-full tw-text-gray-600 dark:tw-text-gray-300"
              >
                {subTitle}
              </Body>
            )}
          </div>
        </div>
        {actions && (
          <div className="tw-flex tw-items-center">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

export default CardHeader
